import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import API_ENDPOINTS from "./config/apiEndpoints";
import { getCurrentUser } from "./services/global.service";
import { useNavigate } from "react-router-dom";
import {
  axiosInstancePublic,
  refreshAccessToken,
  logoutApi,
} from "./services/axiosInstance";
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  subscribeToken,
  setSessionExpiredHandler,
} from "./services/tokenStore";

const AuthContext = createContext();
const ALLOWED_ROLES = ["Custom Fit Portal", "Customfit Admin"];

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginErr] = useState("");
  const [initializing, setInitializing] = useState(true); // bootstrap only (route guard uses this)
  const [loading, setLoading] = useState(false); // login button spinner only
  const [resetToken, setResetToken] = useState("");
  const [userDetails, setUserDetails] = useState(null);
  const [headerObj, setHeaderObj] = useState({});
  const [render, setRender] = useState(0);
  // Access token held in React state. It is mirrored from the in-memory token
  // store, which is what the axios interceptors read (they run outside React).
  const [accessToken, setAccessTokenState] = useState(getAccessToken());
  const didInitRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = subscribeToken(setAccessTokenState);
    setSessionExpiredHandler(() => {
      setUserDetails(null);
      setIsLoggedIn(false);
      navigate("/login");
    });
    return () => {
      unsubscribe();
      setSessionExpiredHandler(null);
    };
  }, [navigate]);

  const endSession = useCallback(async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error("Logout request failed:", error);
    }
    clearAccessToken();
  }, []);

  // Restore the session on page load / reload.
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    const initializeAuth = async () => {
      if (window.location.pathname === "/login") {
        setIsLoggedIn(false);
        setInitializing(false);
        return;
      }
      try {
        await refreshAccessToken();
        const userResponse = await getCurrentUser();
        if (
          userResponse?.isSuccess &&
          ALLOWED_ROLES.includes(userResponse.result?.Role)
        ) {
          setUserDetails(userResponse.result);
          setIsLoggedIn(true);
        } else {
          throw new Error("Invalid session");
        }
      } catch (error) {
        console.error("Failed to restore authentication:", error);
        clearAccessToken();
        setUserDetails(null);
        setIsLoggedIn(false);
      } finally {
        setInitializing(false);
      }
    };
    initializeAuth();
  }, []);

  const login = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await axiosInstancePublic.post(
          `${API_ENDPOINTS.LOGIN.LOGIN_USER}`,
          data,
          { withCredentials: true },
        );

        if (!response?.data?.isSuccess) {
          setLoginErr(
            response?.data?.message ?? "An error occurred during login.",
          );
          setIsLoggedIn(false);
          clearAccessToken();
          setLoading(false);
          return { isSuccess: false };
        }

        const newAccessToken = response?.data?.result?.accessToken;
        if (!newAccessToken) {
          setLoginErr("An error occurred during login.");
          setIsLoggedIn(false);
          await endSession();
          setLoading(false);
          return { isSuccess: false };
        }
        setAccessToken(newAccessToken);

        try {
          const userResponse = await getCurrentUser();
          if (
            userResponse?.isSuccess &&
            ALLOWED_ROLES.includes(userResponse.result?.Role)
          ) {
            setUserDetails(userResponse.result);
            setIsLoggedIn(true);
          } else {
            setLoginErr(
              "You don't have the sufficient privilege to access this portal",
            );
            setIsLoggedIn(false);
            await endSession();
            setLoading(false);
            return { isSuccess: false };
          }
        } catch (err) {
          console.error("Failed to fetch user details post-login:", err);
          setLoginErr("Unable to verify user access. Please try again.");
          setIsLoggedIn(false);
          await endSession();
          setLoading(false);
          return { isSuccess: false };
        }

        setLoading(false);
        return response.data;
      } catch (error) {
        setLoading(false);
        clearAccessToken();
        throw error;
      }
    },
    [endSession],
  );

  const logout = useCallback(async () => {
    await endSession();
    setIsLoggedIn(false);
    setUserDetails(null);
  }, [endSession]);

  const getUserDetails = useCallback(async () => {
    if (userDetails) return userDetails;
    if (initializing) return null;
    try {
      const response = await getCurrentUser();
      if (response?.isSuccess) {
        setUserDetails(response.result);
        return response.result;
      }
      return null;
    } catch (error) {
      console.log(error);
      return null;
    }
  }, [userDetails]);

  const setHeader = useCallback((obj) => setHeaderObj(obj), []);

  const contextValue = useMemo(
    () => ({
      isLoggedIn,
      initializing,
      loading,
      accessToken,
      login,
      logout,
      loginError,
      setIsLoggedIn,
      setLoginErr,
      setLoading,
      resetToken,
      setResetToken,
      getUserDetails,
      setHeader,
      headerObj,
      render,
      setRender,
    }),
    [
      isLoggedIn,
      initializing,
      loading,
      accessToken,
      login,
      logout,
      loginError,
      resetToken,
      getUserDetails,
      setHeader,
      headerObj,
      render,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
